import React, { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { AxiosPromise } from 'axios';
import CommonGrid from 'shared/commonGrid';
import {
  SelectionChangedEvent,
  ColDef,
  ColGroupDef,
  GetRowIdParams,
} from '@ag-grid-enterprise/all-modules';
import { ROWSELECTION } from 'shared/commonGrid/grid-enums';
import { CspConfig } from 'data/cspConfig/cspConfig.types';
import { ServerResponse, UserSettings, GridSettings } from 'data/types';
import isTestEnvironment from 'utils/environment/isTestEnvironment';
import CspConfigListMenu from '../CspConfigListMenu';

type Props = {
  selectedCspConfig?: CspConfig;
  data: CspConfig[];
  hasPrivilegesToCreate: boolean
  hasPrivilegeToDelete: boolean
  hasPrivilegeToView?: boolean
  noResults: boolean,
  beforeLogout: boolean,
  lastUpdatedAt: Date | undefined,
  columnDefs: (ColGroupDef | ColDef)[],
  columnDefsVersion: string,
  suppressColumnVirtualisation?: boolean,
  onRefresh: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onCreateNew: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onSelectionChanged: (event: SelectionChangedEvent) => void;
  onSaveGridSettings: (gridSettings: GridSettings) => void;
  onGetGridSettings: () => { type: string, payload: AxiosPromise<ServerResponse<UserSettings>> };
  onEdit: () => void;
  onDelete: () => void;
  onSelectItemMenuAction: () => void;
}

export const getCspConfigRowId = (params: GetRowIdParams): string => params.data.cspid;

const CspConfigGrid: FunctionComponent<Props> = ({
  hasPrivilegesToCreate,
  hasPrivilegeToDelete,
  hasPrivilegeToView = true,
  data = [],
  noResults,
  beforeLogout,
  lastUpdatedAt,
  columnDefs,
  columnDefsVersion,
  suppressColumnVirtualisation = isTestEnvironment(),
  selectedCspConfig,
  onRefresh,
  onCreateNew,
  onSelectionChanged,
  onSaveGridSettings,
  onGetGridSettings,
  onEdit,
  onDelete,
  onSelectItemMenuAction,
}: Props) => {
  const { t } = useTranslation();

  return (
    <div className="grid-container" style={{ height: 'inherit' }}>
      <CommonGrid
        createNewText={t('cspConfig.grid.createNew')}
        pagination
        enableBrowserTooltips
        rowSelection={ROWSELECTION.SINGLE}
        hasPrivilegeToCreateNew={hasPrivilegesToCreate}
        hasPrivilegeToDelete={hasPrivilegeToDelete}
        hasPrivilegetoView={hasPrivilegeToView}
        rowData={data}
        noResults={noResults}
        isBeforeLogout={beforeLogout}
        columnDefs={columnDefs}
        columnDefsVersion={columnDefsVersion}
        getRowId={getCspConfigRowId}
        suppressColumnVirtualisation={suppressColumnVirtualisation}
        lastUpdatedAt={lastUpdatedAt}
        handleCreateNew={onCreateNew}
        onRefresh={onRefresh}
        onSelectionChanged={onSelectionChanged}
        saveGridSettings={onSaveGridSettings}
        getGridSettingsFromServer={onGetGridSettings}
        onActionMenuItemClick={onSelectItemMenuAction}
      >
        <CspConfigListMenu
          selectedCspConfig={selectedCspConfig}
          hasPrivilegeToCreateNew={hasPrivilegesToCreate}
          hasPrivilegeToDelete={hasPrivilegeToDelete}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      </CommonGrid>
    </div>
  );
};

export default CspConfigGrid;
